import * as React from 'react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import Logo from '../Logo';
import { useRouter } from 'next/router';
import { dataConstants } from '../../../constants';
import useAuth from '../../../hook/auth';

const BRAND_COLOR = 'rgb(42, 45, 100)';
const DRAWER_WIDTH = 288;

const NAV_ITEM_SX = {
  borderRadius: '10px',
  px: 1.5,
  py: 0.875,
  minHeight: 44,
  transition: 'background-color 140ms ease',
  '&:hover': { bgcolor: 'action.hover' },
  '&.Mui-focusVisible': {
    bgcolor: 'action.focus',
    outline: `2px solid ${BRAND_COLOR}`,
    outlineOffset: -2,
  },
  '& .MuiListItemIcon-root': {
    minWidth: 34,
    color: 'text.secondary',
    '& svg': { fontSize: '1rem' },
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    lineHeight: 1.4,
  },
};

const SIGN_OUT_SX = {
  ...NAV_ITEM_SX,
  color: 'error.main',
  '&:hover': { bgcolor: 'rgba(211,47,47,0.06)' },
  '&.Mui-focusVisible': {
    bgcolor: 'rgba(211,47,47,0.06)',
    outline: '2px solid rgba(211,47,47,0.5)',
    outlineOffset: -2,
  },
  '& .MuiListItemIcon-root': {
    ...NAV_ITEM_SX['& .MuiListItemIcon-root'],
    color: 'error.main',
  },
};

const SIGN_IN_SX = {
  ...NAV_ITEM_SX,
  color: BRAND_COLOR,
  '&:hover': { bgcolor: 'rgba(42,45,100,0.06)' },
  '&.Mui-focusVisible': {
    bgcolor: 'rgba(42,45,100,0.06)',
    outline: `2px solid ${BRAND_COLOR}`,
    outlineOffset: -2,
  },
  '& .MuiListItemIcon-root': {
    ...NAV_ITEM_SX['& .MuiListItemIcon-root'],
    color: BRAND_COLOR,
  },
  '& .MuiListItemText-primary': {
    ...NAV_ITEM_SX['& .MuiListItemText-primary'],
    fontWeight: 600,
  },
};

function SectionLabel({ children }) {
  return (
    <Typography
      component="p"
      sx={{
        px: 2,
        pt: 1.75,
        pb: 0.5,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: 'text.disabled',
        userSelect: 'none',
      }}
    >
      {children}
    </Typography>
  );
}

function NavItem({ page, onClick }) {
  return (
    <ListItem disablePadding sx={{ px: 1 }}>
      <ListItemButton onClick={onClick} sx={NAV_ITEM_SX}>
        <ListItemIcon>{page?.icon}</ListItemIcon>
        <ListItemText primary={page?.name} />
      </ListItemButton>
    </ListItem>
  );
}

export default function SwipeableMobileDrawer() {
  const router = useRouter();
  const { user, logout } = useAuth();
  const { reloadUserInfo } = user || {};

  const isPremium = React.useMemo(() => {
    if (!reloadUserInfo?.customAttributes) return false;
    try {
      const { userType } = JSON.parse(reloadUserInfo.customAttributes);
      return userType === 'PREMIUM' || userType === 'PROFESSIONAL';
    } catch {
      return false;
    }
  }, [reloadUserInfo?.customAttributes]);

  const [isOpen, setIsOpen] = React.useState(false);

  const openDrawer = React.useCallback(() => setIsOpen(true), []);
  const closeDrawer = React.useCallback(() => setIsOpen(false), []);

  const navigate = React.useCallback(
    (url) => {
      setIsOpen(false);
      router.push(url);
    },
    [router],
  );

  const handleAuth = React.useCallback(() => {
    setIsOpen(false);
    if (user?.email) {
      logout();
    } else {
      router.push('/auth');
    }
  }, [user?.email, logout, router]);

  const handleContainerKeyDown = React.useCallback((event) => {
    if (event.key === 'Tab' || event.key === 'Shift') return;
    setIsOpen(false);
  }, []);

  const iOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent);

  const profileItems = dataConstants.PREMPROFILE.slice(
    isPremium ? 0 : 4,
    isPremium ? 3 : 6,
  );

  const drawerContent = (
    <Box
      sx={{
        width: DRAWER_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflowX: 'hidden',
      }}
      role="navigation"
      aria-label="Site navigation"
      onKeyDown={handleContainerKeyDown}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={closeDrawer}
          aria-label="Close navigation menu"
          sx={{
            color: 'text.secondary',
            borderRadius: '8px',
            p: 0.75,
            mr: 1.5,
            transition: 'background-color 140ms ease',
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            '&.Mui-focusVisible': {
              outline: `2px solid ${BRAND_COLOR}`,
              outlineOffset: 2,
            },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: '1.125rem' }} />
        </IconButton>
        <Logo color="dark" />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pb: 1.5 }}>
        <List disablePadding sx={{ pt: 1 }}>
          {dataConstants.PAGES.map((page) => (
            <NavItem
              key={page?.name}
              page={page}
              onClick={() => navigate(page.url)}
            />
          ))}
        </List>
        {profileItems.length > 0 && (
          <>
            <SectionLabel>Account</SectionLabel>
            <List disablePadding>
              {profileItems.map((page) => (
                <NavItem
                  key={page?.name}
                  page={page}
                  onClick={() => navigate(page.url)}
                />
              ))}
            </List>
          </>
        )}
        {dataConstants.HELPUS.length > 0 && (
          <>
            <SectionLabel>Support</SectionLabel>
            <List disablePadding>
              {dataConstants.HELPUS.map((page) => (
                <NavItem
                  key={page?.name}
                  page={page}
                  onClick={() => navigate(page.url)}
                />
              ))}
            </List>
          </>
        )}
      </Box>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.default',
          flexShrink: 0,
        }}
      >
        {user?.email ? (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                pt: 1.5,
                pb: 1,
              }}
            >
              <Avatar
                alt={user.displayName || user.email}
                src={user.photoURL || ''}
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: BRAND_COLOR,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    lineHeight: 1.35,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                  }}
                >
                  {user.displayName || user.email}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.25,
                  }}
                >
                  {user.displayName && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.6875rem',
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {user.email}
                    </Typography>
                  )}
                  {isPremium && (
                    <Box
                      component="span"
                      sx={{
                        flexShrink: 0,
                        px: 0.875,
                        py: 0.2,
                        bgcolor: 'rgba(42,45,100,0.09)',
                        color: BRAND_COLOR,
                        borderRadius: '20px',
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        lineHeight: 1.6,
                      }}
                    >
                      Pro
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ px: 1, pb: 1.5 }}>
              <ListItem disablePadding>
                <ListItemButton onClick={handleAuth} sx={SIGN_OUT_SX}>
                  <ListItemIcon>
                    <LogoutRoundedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign out" />
                </ListItemButton>
              </ListItem>
            </Box>
          </>
        ) : (
          <Box sx={{ px: 1, py: 1.5 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={handleAuth} sx={SIGN_IN_SX}>
                <ListItemIcon>
                  <LoginRoundedIcon />
                </ListItemIcon>
                <ListItemText primary="Sign in" />
              </ListItemButton>
            </ListItem>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <React.Fragment>
      <IconButton
        size="large"
        aria-label="Open navigation menu"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        color="inherit"
        sx={{ display: { md: 'none' } }}
        onClick={openDrawer}
      >
        <MenuIcon />
      </IconButton>
      <SwipeableDrawer
        disableBackdropTransition={!iOS}
        disableDiscovery={iOS}
        anchor="left"
        open={isOpen}
        onClose={closeDrawer}
        onOpen={openDrawer}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRight: '1px solid',
            borderColor: 'divider',
            boxShadow: '8px 0 32px rgba(0,0,0,0.07)',
          },
        }}
      >
        {drawerContent}
      </SwipeableDrawer>
    </React.Fragment>
  );
}